import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

/* ------------------- Processor-specific program VPAs (preauth / issuance-time) -------------------
 *
 * YESBANK    -> preauth service  : ${API_URL}/v1/preauth/programVpa     (bare JSON responses)
 * ICICI_CBDC -> programs service : ${API_URL}/v1/programs/program-vpas  (responses wrapped in { status, data })
 *
 * Every call goes through the adapter below so the components never need to know
 * which backend a processor uses.
 */

export type VpaBrand = "YESBANK" | "ICICI_CBDC";

export interface ProgramVpa {
  id: number;
  programCode: string;
  vpa: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Result of the Update Rule sync that the programs service runs after a change (ICICI_CBDC only)
export interface RuleSyncSummary {
  programCode: string;
  total?: number;
  alreadyInSync?: number;
  updated?: number;
  failed?: number;
  // Set when the program has no rules to update (e.g. not an ICICI_CBDC program)
  skipped?: boolean;
  reason?: string;
  // True when the sync itself could not run (details are in the server logs)
  error?: boolean;
}

// ICICI_CBDC runs the bank Update Rule sync inside the request, so it needs far more time
const DEFAULT_TIMEOUT_MS = 10000;
const CBDC_TIMEOUT_MS = 180000;

const getPreauthBaseUrl = () => window.env.PREAUTH_API_URL || `${window.env.API_URL}/v1/preauth`;

const request = (brand: VpaBrand, config: { url: string; method: "GET" | "POST" | "PUT"; data?: unknown; params?: unknown }) => {
  const token = localStorage.getItem("accessToken");
  const baseURL = brand === "YESBANK" ? `${getPreauthBaseUrl()}/programVpa` : `${window.env.API_URL}/v1/programs/program-vpas`;
  return axios.request({
    baseURL,
    timeout: brand === "ICICI_CBDC" ? CBDC_TIMEOUT_MS : DEFAULT_TIMEOUT_MS,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    ...config,
  });
};

// Program codes are stored and matched in lowercase by both backends
const normalizeProgramCode = (programCode: string) => programCode.trim().toLowerCase();

const errorMessage = (error: any, fallback: string): string => error?.response?.data?.message || fallback;

// ICICI_CBDC wraps payloads as { status, data }; preauth returns them bare
const unwrap = (brand: VpaBrand, responseData: any) => (brand === "ICICI_CBDC" ? responseData?.data : responseData);

// Only ICICI_CBDC returns a sync summary; one entry per changed program (always this program here)
const extractSync = (brand: VpaBrand, responseData: any): RuleSyncSummary | null => {
  if (brand !== "ICICI_CBDC") return null;
  const sync = responseData?.data?.sync;
  return Array.isArray(sync) && sync.length ? (sync[0] as RuleSyncSummary) : null;
};

async function loadVpas(brand: VpaBrand, programCode: string): Promise<ProgramVpa[]> {
  const response = await request(brand, { url: `/program/${encodeURIComponent(normalizeProgramCode(programCode))}`, method: "GET" });
  return (unwrap(brand, response.data) || []) as ProgramVpa[];
}

interface MutationResult {
  vpas: ProgramVpa[];
  sync: RuleSyncSummary | null;
  // True when the write succeeded but reloading the list failed (state keeps the old list)
  refreshFailed?: boolean;
}

interface VpaArgs {
  brand: VpaBrand;
  programCode: string;
}

// Lists every VPA (active and inactive) of a program
export const fetchPreauthVpas = createAsyncThunk<ProgramVpa[], VpaArgs, { rejectValue: string }>(
  "preauthVpa/fetch",
  async ({ brand, programCode }, { rejectWithValue }) => {
    try {
      return await loadVpas(brand, programCode);
    } catch (error: any) {
      return rejectWithValue(errorMessage(error, "Failed to fetch VPAs"));
    }
  },
);

// Adds one VPA, or many (bulk upload). Always active; the caller has already validated/normalised them.
export const addPreauthVpas = createAsyncThunk<
  MutationResult,
  VpaArgs & { vpas: string[] },
  { rejectValue: string }
>("preauthVpa/add", async ({ brand, programCode, vpas }, { rejectWithValue }) => {
  try {
    const code = normalizeProgramCode(programCode);
    const entries = vpas.map((vpa) => ({ programCode: code, vpa, isActive: true }));
    const response = await request(brand, { url: "/", method: "POST", data: entries.length === 1 ? entries[0] : entries });
    const sync = extractSync(brand, response.data);
    // The write succeeded; if only the refresh fails, surface the stale list instead of a false failure
    const refreshed = await loadVpas(brand, programCode).catch(() => null);
    return { vpas: refreshed ?? [], sync, refreshFailed: !refreshed };
  } catch (error: any) {
    return rejectWithValue(errorMessage(error, "Failed to add VPA"));
  }
});

// Only the status can be changed after creation (both backends enforce this)
export const updatePreauthVpaStatus = createAsyncThunk<
  MutationResult,
  VpaArgs & { id: number; isActive: boolean },
  { rejectValue: string }
>("preauthVpa/updateStatus", async ({ brand, programCode, id, isActive }, { rejectWithValue }) => {
  try {
    const url = brand === "ICICI_CBDC" ? `/${id}/status` : `/${id}`;
    const response = await request(brand, { url, method: "PUT", data: { isActive } });
    const sync = extractSync(brand, response.data);
    const refreshed = await loadVpas(brand, programCode).catch(() => null);
    return { vpas: refreshed ?? [], sync, refreshFailed: !refreshed };
  } catch (error: any) {
    return rejectWithValue(errorMessage(error, "Failed to update VPA status"));
  }
});

interface PreauthVpaState {
  vpas: ProgramVpa[];
  listLoading: boolean;
  listError: string | null;
  saving: boolean;
  saveError: string | null;
  // Latest Update Rule sync outcome (ICICI_CBDC); null when none ran
  syncResult: RuleSyncSummary | null;
}

const initialState: PreauthVpaState = {
  vpas: [],
  listLoading: false,
  listError: null,
  saving: false,
  saveError: null,
  syncResult: null,
};

const preauthVpaSlice = createSlice({
  name: "preauthVpa",
  initialState,
  reducers: {
    clearPreauthVpas(state) {
      state.vpas = [];
      state.listError = null;
      state.saveError = null;
      state.syncResult = null;
    },
    clearPreauthVpaSaveError(state) {
      state.saveError = null;
    },
    clearPreauthVpaSyncResult(state) {
      state.syncResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreauthVpas.pending, (state) => {
        state.listLoading = true;
        state.listError = null;
        state.vpas = [];
      })
      .addCase(fetchPreauthVpas.fulfilled, (state, action: PayloadAction<ProgramVpa[]>) => {
        state.listLoading = false;
        state.vpas = action.payload;
      })
      .addCase(fetchPreauthVpas.rejected, (state, action) => {
        state.listLoading = false;
        state.listError = action.payload || "Failed to fetch VPAs";
      })

      .addCase(addPreauthVpas.pending, (state) => {
        state.saving = true;
        state.saveError = null;
        state.syncResult = null;
      })
      .addCase(addPreauthVpas.fulfilled, (state, action: PayloadAction<MutationResult>) => {
        state.saving = false;
        if (!action.payload.refreshFailed) state.vpas = action.payload.vpas;
        state.syncResult = action.payload.sync;
      })
      .addCase(addPreauthVpas.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || "Failed to add VPA";
      })

      .addCase(updatePreauthVpaStatus.pending, (state) => {
        state.saving = true;
        state.saveError = null;
        state.syncResult = null;
      })
      .addCase(updatePreauthVpaStatus.fulfilled, (state, action: PayloadAction<MutationResult>) => {
        state.saving = false;
        if (!action.payload.refreshFailed) state.vpas = action.payload.vpas;
        state.syncResult = action.payload.sync;
      })
      .addCase(updatePreauthVpaStatus.rejected, (state, action) => {
        state.saving = false;
        state.saveError = action.payload || "Failed to update VPA status";
      });
  },
});

export const { clearPreauthVpas, clearPreauthVpaSaveError, clearPreauthVpaSyncResult } = preauthVpaSlice.actions;

export default preauthVpaSlice.reducer;
